import { useMemo, useRef, useState } from "react"
import { cloneDeep } from "lodash"

import SectionContainer from "@/app/report/components/SectionContainer"
import MultipleChart from "@/app/report/components/chart/MultipleChart"
import VerticalPlayerChart from "@/app/report/components/chart/VerticalPlayerChart"
import { resolveGraphOrientation } from "@/app/report/common/sections/graph"
import SectionMenu from "@/app/report/components/SectionMenu"
import { ExportChartRefType, MenuActionType } from "@/app/report/common/type"
import { SectionGraph } from "@/app/report/common/constructor"
import {
  DEFAULT_EXPORT_OPTIONS,
  REPORT_TYPE,
  VALUE_POSITION_TYPE,
} from "@/app/report/common/constants"
import ExportChartDialog from "@/app/report/components/dialog/ExportChartDialog"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { usePositionRank } from "@/hooks/usePositionRank"
import { buildReportSortKey, createReportSortStore } from "@/stores/reportSort"
import { sortChartRows } from "@/app/report/common/export/sortRows"

export default function ChartByActivity({
  reportSection,
  loading,
  sectionIndex = 0,
  reportType = REPORT_TYPE.TREND,
}: {
  reportSection: SectionGraph
  loading: boolean
  sectionIndex?: number
  reportType?: REPORT_TYPE
}) {
  const storeKey = buildReportSortKey(reportType, sectionIndex, 0)
  // 화면 정렬은 리포트 스코프 store 에 보존(템플릿 미저장). 없으면 템플릿(charts[0].sort) seed.
  const storedSort = createReportSortStore((state) => state.sorts[storeKey])
  const setSort = createReportSortStore((state) => state.setSort)

  const sortValue: string[] = storedSort
    ? [storedSort.key, storedSort.order]
    : reportSection?.charts[0]?.sort
      ? [reportSection.charts[0].sort.key, reportSection.charts[0].sort.order.toLowerCase()]
      : []

  const [exportFileType, setExportFileType] = useState<string>("")
  const [currentDecimalPoints, setCurrentDecimalPoints] = useState<number>(1)
  const [currentAnnotationOptions, setCurrentAnnotationOptions] = useState<VALUE_POSITION_TYPE>(
    VALUE_POSITION_TYPE.HIDE_VALUES,
  )

  const chartRef = useRef<ExportChartRefType>(null)

  const translateKey = useTranslateMetricKey()
  const positionRank = usePositionRank()

  const menuItems = useMemo(() => {
    /* chart by activity: value, date
    export pdf, png image는 공통 필수 */
    const menuOptions = [] as MenuActionType[]
    menuOptions.push({
      name: "value",
      value: "value",
      group: "sort",
      children: reportSection?.charts[0].settings.yAxis.map((yAxis) => {
        const newMetricName = translateKey(yAxis.metric)

        return {
          name: newMetricName,
          value: yAxis.metric,
          color: yAxis.color,
          children: [
            {
              name: "maxMin",
              value: `${yAxis.metric}MaxMin`,
              func: () => {
                setSort(storeKey, { key: yAxis.metric, order: "desc" })
              },
            },
            {
              name: "minMax",
              value: `${yAxis.metric}MinMax`,
              func: () => {
                setSort(storeKey, { key: yAxis.metric, order: "asc" })
              },
            },
          ],
        }
      }),
    })
    menuOptions.push({
      name: "date",
      value: "date",
      group: "sort",
      children: [
        {
          name: "lowToHigh",
          value: "dateAscending",
          func: () => setSort(storeKey, { key: "date", order: "asc" }),
        },
        {
          name: "highToLow",
          value: "dateDescending",
          func: () => setSort(storeKey, { key: "date", order: "desc" }),
        },
      ],
    })
    menuOptions.push(
      ...DEFAULT_EXPORT_OPTIONS.map((option) => ({
        ...option,
        func: () => {
          setExportFileType(option.value)
        },
      })),
    )

    return menuOptions
  }, [reportSection])

  const chartData = useMemo(() => {
    if (!sortValue.length) {
      return reportSection?.charts[0]
    }

    const newChartData = cloneDeep(reportSection?.charts[0])
    newChartData.data = sortChartRows(
      newChartData.data,
      newChartData.axisY,
      { key: sortValue[0], order: sortValue[1] as "asc" | "desc" },
      positionRank,
    )

    return newChartData
  }, [sortValue, reportSection, positionRank])

  const setOpen = (open: boolean) => {
    if (!open) {
      setExportFileType("")
    }
  }

  return (
    <>
      <SectionContainer
        title={reportSection?.charts[0].settings.title}
        sectionMenu={
          reportSection?.charts[0].data.length ? <SectionMenu menuItems={menuItems} /> : undefined
        }>
        {/* 세로(액티비티 세로 나열)는 별도 렌더러 — 템플릿 Graph 설정의 orientation 을 따른다 (#185) */}
        {resolveGraphOrientation(chartData.settings) === "vertical" ? (
          <VerticalPlayerChart chartData={chartData} loading={loading} ref={chartRef} />
        ) : (
          <MultipleChart chartData={chartData} loading={loading} ref={chartRef} />
        )}
      </SectionContainer>

      <ExportChartDialog
        open={!!exportFileType}
        setOpen={setOpen}
        decimalPoints={currentDecimalPoints}
        annotationOptions={currentAnnotationOptions}
        onConfirm={() => {
          // TODO export action
          chartRef.current?.exportFile({
            exportType: exportFileType as "png" | "pdf",
            magnification: 1.0,
            reportType: window.location.pathname.includes(REPORT_TYPE.DAILY)
              ? REPORT_TYPE.DAILY
              : REPORT_TYPE.TREND,
            decimalPoints: currentDecimalPoints,
            chartAnnotation: currentAnnotationOptions,
          })
        }}
        onChangeDecimalPoints={(value: number) => setCurrentDecimalPoints(value)}
        onChangeAnnotationOptions={(value: VALUE_POSITION_TYPE) =>
          setCurrentAnnotationOptions(value)
        }
      />
    </>
  )
}
